import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import getStrategiesTool from './getStrategiesTool';
import setStrategyTool from './setStrategyTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateProductCard from '../../utils/product/getOrCreateProductCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import {RelationType} from 'common/src/modules/relatedItems/relatedItems.types';
import setRelatedItemTool from './setRelatedItemsTool';
import setFeedTool from './setFeedTool';
import setSpecificItemsTool from './setSpecificItemsTool';
import searchMatchingSignalsTool from './searchMatchingSignalsTool';
import setMatchingConditionsTool from './setMatchingConditionsTool';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import searchFeedColumnsTool from './searchFeedColumnsTool';

const cardSpecificInstructions = `- YOU MUST ALWAYS Use the ${V2CardAgentToolName.GetStrategies} tool to find the best product selection strategy, following the user's request.
- Before setting the strategy, you need to verify the user has provided all the mandatory fields for the strategy. Use the strategy's description to determine the mandatory fields.
- Once the you found the matching strategy and validated it, use the ${V2CardAgentToolName.SetStrategy} tool to set the strategy.
- If the strategy ID is ${ProductSelectionStrategyType.ProductMatching}, you need to use the ${V2CardAgentToolName.SearchFeedColumns} and ${V2CardAgentToolName.SearchMatchingSignals} tools to search for the best feed columns and matching signals, following the user's request.
- If the strategy is retargeting oriented, you need to use the ${CampaignSetupAgentToolName.AskAboutObjects} tool to search for the best product set, following the user's request. and than than the ${V2CardAgentToolName.SetRelatedItemLogic} tool to set the related item logic (Product Set, Top Items, Category Top Items).`;

export default function addOrUpdateProductCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ProductCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateProductCardV2,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.PRODUCTS]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.PRODUCTS,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Product tools
        getStrategiesTool(store),
        setStrategyTool(),
        setFeedTool(),
        setRelatedItemTool(RelationType.ProductSet),
        setRelatedItemTool(RelationType.TopItems),
        setRelatedItemTool(RelationType.CategoryTopItems),
        setSpecificItemsTool(),

        searchFeedColumnsTool(),
        searchMatchingSignalsTool(store),
        setMatchingConditionsTool(store),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateProductCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateProductCardV2,
    displayName: 'Working on product card',
    description:
      'Add or update a product selection card. The card selects products from product feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure product selection strategy, configure matching conditions for custom matching strategy, assign a feed, ' +
      'assign product-set/top-items/category-top-items lists if strategy supports it, or assign a specific item for the specific items strategy.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
